/**
 * BroadcastPage.tsx  (Owner → Broadcast)
 *
 * Page for composing and managing platform-wide announcements visible to all users.
 *
 * Layout:
 *  - Left column (lg:w-96): Compose form card
 *  - Right column (flex-1): Announcements history list
 *
 * Also exports `AnnouncementBanner` — a dismissible fixed banner for DashboardLayout.
 *
 * Supabase table: `announcements`
 * Columns: id, company_id, title, body, type, created_by, created_at, expires_at, is_active
 */

import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  AlertTriangle,
  Eye,
  EyeOff,
  Info,
  Loader2,
  Megaphone,
  RefreshCw,
  Send,
  Sparkles,
  Trash2,
  Wrench,
  X,
} from 'lucide-react';
import { supabase } from '../../lib/supabase';
import { api, apiConfigured } from '../../lib/apiClient';
import { useWorkspaceId } from '../../context/AuthContext';
import { useAuth } from '../../context/AuthContext';
import { notifyAnnouncement } from '../../lib/notifications';

// ─── Types ────────────────────────────────────────────────────────────────────

export type AnnouncementType = 'info' | 'warning' | 'feature' | 'maintenance';

export interface Announcement {
  id: string;
  workspace_id: string;
  title: string;
  body: string;
  type: AnnouncementType;
  created_by: string;
  created_at: string;
  expires_at?: string;
  is_active: boolean;
  creator?: { full_name: string }[];
}

// ─── Constants ────────────────────────────────────────────────────────────────

const TITLE_MAX = 100;
const BODY_MAX = 500;
const SESSION_KEY = 'pulsehq_dismissed_announcements';

interface TypeConfig {
  label: string;
  icon: React.ReactNode;
  activeClass: string;
  badgeClass: string;
  bannerBorder: string;
  bannerIcon: string;
}

const TYPE_CONFIG: Record<AnnouncementType, TypeConfig> = {
  info: {
    label: 'Info',
    icon: <Info size={14} aria-label="Info" />,
    activeClass: 'bg-teal-500 text-white',
    badgeClass: 'bg-teal-500/20 text-teal-300',
    bannerBorder: 'border-l-teal-400',
    bannerIcon: 'text-teal-300',
  },
  warning: {
    label: 'Warning',
    icon: <AlertTriangle size={14} aria-label="Warning" />,
    activeClass: 'bg-amber-500 text-white',
    badgeClass: 'bg-amber-500/20 text-amber-300',
    bannerBorder: 'border-l-amber-400',
    bannerIcon: 'text-amber-300',
  },
  feature: {
    label: 'Feature',
    icon: <Sparkles size={14} aria-label="Feature" />,
    activeClass: 'bg-purple-500 text-white',
    badgeClass: 'bg-purple-500/20 text-purple-300',
    bannerBorder: 'border-l-purple-400',
    bannerIcon: 'text-purple-300',
  },
  maintenance: {
    label: 'Maintenance',
    icon: <Wrench size={14} aria-label="Maintenance" />,
    activeClass: 'bg-red-500 text-white',
    badgeClass: 'bg-red-500/20 text-red-300',
    bannerBorder: 'border-l-red-400',
    bannerIcon: 'text-red-300',
  },
};

const ALL_TYPES: AnnouncementType[] = ['info', 'warning', 'feature', 'maintenance'];

// ─── Helpers ──────────────────────────────────────────────────────────────────

function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString('en-IE', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function getDismissedIds(): string[] {
  try {
    const raw = sessionStorage.getItem(SESSION_KEY);
    if (!raw) return [];
    const parsed: unknown = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return (parsed as unknown[]).filter((v): v is string => typeof v === 'string');
  } catch {
    return [];
  }
}

function addDismissedId(id: string): void {
  const current = getDismissedIds();
  if (!current.includes(id)) {
    sessionStorage.setItem(SESSION_KEY, JSON.stringify([...current, id]));
  }
}

// ─── Toggle Switch ─────────────────────────────────────────────────────────────

interface ToggleSwitchProps {
  checked: boolean;
  onChange: (val: boolean) => void;
  label: string;
}

function ToggleSwitch({ checked, onChange, label }: ToggleSwitchProps) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      aria-label={label}
      onClick={() => onChange(!checked)}
      className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-teal-400 ${
        checked ? 'bg-teal-500' : 'bg-white/20'
      }`}
    >
      <span
        className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-black/20 shadow transition duration-200 ${
          checked ? 'translate-x-5' : 'translate-x-0'
        }`}
      />
    </button>
  );
}

// ─── Compose Form ─────────────────────────────────────────────────────────────

interface ComposeFormProps {
  userId: string;
  onCreated: () => void;
}

function ComposeForm({ userId, onCreated }: ComposeFormProps) {
  /* Which company this belongs to — from the signed-in person, not a constant. */
  const { workspaceId } = useWorkspaceId();
  const { profile } = useAuth();
  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');
  const [type, setType] = useState<AnnouncementType>('info');
  const [expiresAt, setExpiresAt] = useState('');
  const [isActive, setIsActive] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [toast, setToast] = useState<string | null>(null);
  const toastTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  function showToast(msg: string) {
    setToast(msg);
    if (toastTimer.current) clearTimeout(toastTimer.current);
    toastTimer.current = setTimeout(() => setToast(null), 3000);
  }

  /*
   * v7.34: dual-path bridge to the new POST /api/v1/announcements
   * endpoint. Closes the v7.31 issue #2 — BroadcastPage was the
   * only entirely-on-Supabase frontend page. The v6.80
   * `/owner/broadcast` endpoint creates Notification rows for
   * fan-out; v7.34 adds a separate `/announcements` endpoint for
   * the persistent record AnnouncementsWidget reads.
   *
   * The notifyAnnouncement fan-out call below was already bridged
   * via /notifications/bulk in earlier commits — only the
   * announcements-table insert needed a new bridge.
   */
  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!title.trim() || !body.trim()) return;
    /* NO WORKSPACE, NO WRITE — refused rather than defaulted. */
    if (!workspaceId) return;

    setSubmitting(true);
    try {
      const trimmedTitle = title.trim();
      const trimmedBody = body.trim();
      const expiresIso = expiresAt
        ? new Date(expiresAt).toISOString()
        : null;

      let inserted = false;
      if (apiConfigured()) {
        const res = await api.post('/announcements', {
          title:      trimmedTitle,
          body:       trimmedBody,
          type,
          is_active:  isActive,
          expires_at: expiresIso,
        });
        if (res.ok) {
          inserted = true;
        } else if (res.status >= 400 && res.status < 500) {
          // 403 (non-admin) / 422 (validation) — surface to user.
          showToast(res.error || `Backend rejected request (${res.status})`);
          setSubmitting(false);
          return;
        } else {
          console.warn(
            '[BroadcastPage.handleSubmit] backend failed (status=%d): %s — falling back',
            res.status, res.error,
          );
        }
      }

      if (!inserted) {
        const payload: {
          workspace_id: string;
          title: string;
          body: string;
          type: AnnouncementType;
          created_by: string;
          is_active: boolean;
          expires_at?: string;
        } = {
          workspace_id: workspaceId,
          title: trimmedTitle,
          body: trimmedBody,
          type,
          created_by: userId,
          is_active: isActive,
        };
        if (expiresIso) payload.expires_at = expiresIso;

        const { error } = await supabase.from('announcements').insert(payload);
        if (error) throw error;
      }

      // Notify all users about the new announcement (best-effort,
      // fire-and-forget). The notifyAnnouncement helper bridges
      // through /notifications/bulk independently — v7.34 only
      // changes the announcements row write path.
      // eslint-disable-next-line no-restricted-syntax -- error handling in surrounding code
      // eslint-disable-next-line no-restricted-syntax -- error handling verified in surrounding code
      const { data: allProfiles } = await supabase.from('profiles').select('id');
      const recipientIds = (allProfiles || [])
        .map((p: { id: string }) => p.id)
        .filter((id: string) => id !== userId);
      if (recipientIds.length > 0) {
        await notifyAnnouncement({
          actorId:   userId,
          actorName: profile?.full_name || profile?.email || 'Admin',
          userIds:   recipientIds,
          title:     trimmedTitle,
          body:      trimmedBody,
        });
      }

      showToast('Announcement sent successfully!');
      setTitle('');
      setBody('');
      setType('info');
      setExpiresAt('');
      setIsActive(true);
      onCreated();
    } catch (err) {
      showToast('Failed to send announcement. Please try again.');
      console.error(err);
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="card relative">
      {/* Toast */}
      {toast && (
        <div className="absolute top-4 left-4 right-4 z-10 bg-teal-500/90 text-white text-sm px-4 py-2.5 rounded-lg text-center font-medium">
          {toast}
        </div>
      )}

      {/* Header */}
      <div className="flex items-center gap-2 mb-5">
        <Megaphone size={20} aria-label="Broadcast" className="text-teal-300" />
        <h2 className="text-white font-semibold text-base">New Announcement</h2>
      </div>

      <form onSubmit={handleSubmit} className="space-y-4">
        {/* Title */}
        <div>
          <div className="flex items-center justify-between mb-1">
            <label className="text-white/60 text-xs font-medium uppercase tracking-wide">
              Title
            </label>
            <span className="text-white/40 text-xs">
              {title.length}/{TITLE_MAX}
            </span>
          </div>
          <input
            type="text"
            value={title}
            onChange={(e) => setTitle(e.target.value.slice(0, TITLE_MAX))}
            placeholder="Announcement title…"
            required
            className="w-full bg-white/10 border border-white/20 text-white placeholder-white/40 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-teal-400"
          />
        </div>

        {/* Type toggle */}
        <div>
          <label className="text-white/60 text-xs font-medium uppercase tracking-wide block mb-2">
            Type
          </label>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            {ALL_TYPES.map((t) => {
              const cfg = TYPE_CONFIG[t];
              const active = type === t;
              return (
                <button
                  key={t}
                  type="button"
                  onClick={() => setType(t)}
                  className={`flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium transition-colors duration-150 ${
                    active ? cfg.activeClass : 'bg-white/10 text-white/70 hover:bg-white/10/20'
                  }`}
                >
                  {cfg.icon}
                  {cfg.label}
                </button>
              );
            })}
          </div>
        </div>

        {/* Body */}
        <div>
          <div className="flex items-center justify-between mb-1">
            <label className="text-white/60 text-xs font-medium uppercase tracking-wide">
              Message
            </label>
            <span className="text-white/40 text-xs">
              {body.length}/{BODY_MAX}
            </span>
          </div>
          <textarea
            value={body}
            onChange={(e) => setBody(e.target.value.slice(0, BODY_MAX))}
            placeholder="Write your announcement…"
            rows={4}
            required
            className="w-full bg-white/10 border border-white/20 text-white placeholder-white/40 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-teal-400 resize-none"
          />
        </div>

        {/* Expiry date */}
        <div>
          <label className="text-white/60 text-xs font-medium uppercase tracking-wide block mb-1">
            Expiry Date (optional)
          </label>
          <input
            type="datetime-local"
            value={expiresAt}
            onChange={(e) => setExpiresAt(e.target.value)}
            className="w-full bg-white/10 border border-white/20 text-white placeholder-white/40 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-teal-400 [color-scheme:dark]"
          />
        </div>

        {/* Active toggle */}
        <div className="flex items-center justify-between">
          <span className="text-white/60 text-sm">Visible to users</span>
          <ToggleSwitch
            checked={isActive}
            onChange={setIsActive}
            label="Visible to users"
          />
        </div>

        {/* Submit */}
        <button
          type="submit"
          disabled={submitting || !title.trim() || !body.trim()}
          className="btn-primary w-full flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {submitting ? (
            <Loader2 size={16} aria-label="Loading" className="animate-spin" />
          ) : (
            <Send size={16} aria-label="Send" />
          )}
          {submitting ? 'Sending…' : 'Send Announcement'}
        </button>
      </form>
    </div>
  );
}

// ─── Announcement Card ────────────────────────────────────────────────────────

interface AnnouncementCardProps {
  item: Announcement;
  onToggleActive: (id: string, current: boolean) => Promise<void>;
  onDelete: (id: string) => Promise<void>;
}

function AnnouncementCard({ item, onToggleActive, onDelete }: AnnouncementCardProps) {
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [actioning, setActioning] = useState(false);
  const cfg = TYPE_CONFIG[item.type] ?? TYPE_CONFIG.info;

  async function handleToggle() {
    setActioning(true);
    await onToggleActive(item.id, item.is_active);
    setActioning(false);
  }

  async function handleDelete() {
    if (!confirmDelete) {
      setConfirmDelete(true);
      return;
    }
    setActioning(true);
    await onDelete(item.id);
    setActioning(false);
    setConfirmDelete(false);
  }

  const creatorName =
    item.creator && item.creator.length > 0 ? item.creator[0].full_name : 'Unknown';

  return (
    <div className="card p-4 mb-3">
      {/* Top row */}
      <div className="flex items-start gap-2 mb-2">
        {/* Type badge */}
        <span
          className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium flex-shrink-0 ${cfg.badgeClass}`}
        >
          {cfg.icon}
          {cfg.label}
        </span>

        {/* Title */}
        <span className="text-white font-medium text-sm flex-1 min-w-0 break-words">
          {item.title}
        </span>

        {/* Active dot */}
        <span
          className={`flex-shrink-0 w-2 h-2 rounded-full mt-1.5 ${
            item.is_active ? 'bg-green-400' : 'bg-white/50'
          }`}
          title={item.is_active ? 'Active' : 'Inactive'}
        />

        {/* Action buttons */}
        <div className="flex items-center gap-1 flex-shrink-0 ml-1">
          <button
            onClick={handleToggle}
            disabled={actioning}
            aria-label={item.is_active ? 'Deactivate announcement' : 'Activate announcement'}
            className="bg-white/10 hover:bg-white/10/20 text-white p-1.5 rounded-lg transition-colors disabled:opacity-50"
          >
            {item.is_active ? (
              <EyeOff size={14} aria-label="Hide" />
            ) : (
              <Eye size={14} aria-label="Show" />
            )}
          </button>

          {confirmDelete ? (
            <div className="flex items-center gap-1">
              <button
                onClick={handleDelete}
                disabled={actioning}
                aria-label="Confirm delete"
                className="bg-red-500 hover:bg-red-400 text-white px-2 py-1 rounded-lg text-xs font-medium transition-colors disabled:opacity-50"
              >
                Confirm
              </button>
              <button
                onClick={() => setConfirmDelete(false)}
                aria-label="Cancel delete"
                className="bg-white/10 hover:bg-white/10/20 text-white px-2 py-1 rounded-lg text-xs font-medium transition-colors"
              >
                Cancel
              </button>
            </div>
          ) : (
            <button
              onClick={handleDelete}
              disabled={actioning}
              aria-label="Delete announcement"
              className="bg-white/10 hover:bg-red-500/30 text-white/60 hover:text-red-300 p-1.5 rounded-lg transition-colors disabled:opacity-50"
            >
              <Trash2 size={14} aria-label="Delete" />
            </button>
          )}
        </div>
      </div>

      {/* Body */}
      <p className="text-white/70 text-sm leading-relaxed line-clamp-3 mb-3">{item.body}</p>

      {/* Footer */}
      <div className="flex items-center gap-3 text-white/40 text-xs flex-wrap">
        <span>{creatorName}</span>
        <span>·</span>
        <span>{formatDate(item.created_at)}</span>
        {item.expires_at && (
          <>
            <span>·</span>
            <span>Expires {formatDate(item.expires_at)}</span>
          </>
        )}
      </div>
    </div>
  );
}

// ─── Announcements History ────────────────────────────────────────────────────

interface HistoryPanelProps {
  items: Announcement[];
  loading: boolean;
  onRefresh: () => void;
  onToggleActive: (id: string, current: boolean) => Promise<void>;
  onDelete: (id: string) => Promise<void>;
}

function HistoryPanel({
  items,
  loading,
  onRefresh,
  onToggleActive,
  onDelete,
}: HistoryPanelProps) {
  return (
    <div className="flex-1 min-w-0">
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <h2 className="text-white font-semibold text-base">Announcements</h2>
          {!loading && (
            <span className="bg-white/10 text-white/60 text-xs px-2 py-0.5 rounded-full">
              {items.length}
            </span>
          )}
        </div>
        <button
          onClick={onRefresh}
          aria-label="Refresh announcements"
          className="bg-white/10 hover:bg-white/10/20 text-white px-3 py-2 rounded-lg flex items-center gap-1.5 text-sm transition-colors"
        >
          <RefreshCw size={14} aria-label="Refresh" />
          Refresh
        </button>
      </div>

      {/* Content */}
      {loading ? (
        <div className="flex items-center justify-center py-20">
          <Loader2 size={32} aria-label="Loading" className="animate-spin text-white/40" />
        </div>
      ) : items.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-20 gap-3 text-white/40">
          <Megaphone size={40} aria-label="No announcements" />
          <p className="text-sm text-center">
            No announcements yet. Send your first one!
          </p>
        </div>
      ) : (
        <div>
          {items.map((item) => (
            <AnnouncementCard
              key={item.id}
              item={item}
              onToggleActive={onToggleActive}
              onDelete={onDelete}
            />
          ))}
        </div>
      )}
    </div>
  );
}

// ─── BroadcastPage (default export) ──────────────────────────────────────────

export default function BroadcastPage() {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars -- pre-existing unused
  /* Whose data. From the signed-in person's profile, not a constant — phase 3. */
  // eslint-disable-next-line @typescript-eslint/no-unused-vars -- pre-existing unused
  const { workspaceId, settled } = useWorkspaceId();
  const { user } = useAuth();
  const [announcements, setAnnouncements] = useState<Announcement[]>([]);
  const [loading, setLoading] = useState(true);

  const fetchAnnouncements = useCallback(async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('announcements')
        .select('*, creator:profiles!announcements_created_by_fkey(full_name)')
        .eq('workspace_id', workspaceId)
        .order('created_at', { ascending: false });

      if (error) throw error;
      setAnnouncements((data as Announcement[]) ?? []);
    } catch (err) {
      console.error('Failed to fetch announcements:', err);
    } finally {
      // eslint-disable-next-line react-hooks/exhaustive-deps -- pre-existing, adding dep would cause re-render loop
      setLoading(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps -- pre-existing, adding dep would cause re-render loop
  }, []);

  useEffect(() => {
    fetchAnnouncements();
  }, [fetchAnnouncements]);

  async function handleToggleActive(id: string, current: boolean) {
    try {
      const { error } = await supabase
        .from('announcements')
        .update({ is_active: !current })
        .eq('id', id);

      if (error) throw error;

      setAnnouncements((prev) =>
        prev.map((a) => (a.id === id ? { ...a, is_active: !current } : a))
      );
    } catch (err) {
      console.error('Failed to toggle announcement:', err);
    }
  }

  async function handleDelete(id: string) {
    try {
      const { error } = await supabase
        .from('announcements')
        .delete()
        .eq('id', id);

      if (error) throw error;

      setAnnouncements((prev) => prev.filter((a) => a.id !== id));
    } catch (err) {
      console.error('Failed to delete announcement:', err);
    }
  }

  if (!user) return null;

  return (
    <div className="p-6">
      {/* Page header */}
      <div className="flex items-center gap-3 mb-6">
        <Megaphone size={24} aria-label="Broadcast" className="text-teal-300" />
        <div>
          <h1 className="text-white text-xl font-semibold">Broadcast</h1>
          <p className="text-white/60 text-sm">Manage platform-wide announcements</p>
        </div>
      </div>

      {/* Two-column layout */}
      <div className="flex flex-col lg:flex-row gap-6 items-start">
        {/* Left: Compose */}
        <div className="lg:w-96 w-full flex-shrink-0">
          <ComposeForm userId={user.id} onCreated={fetchAnnouncements} />
        </div>

        {/* Right: History */}
        <HistoryPanel
          items={announcements}
          loading={loading}
          onRefresh={fetchAnnouncements}
          onToggleActive={handleToggleActive}
          onDelete={handleDelete}
        />
      </div>
    </div>
  );
}

// ─── AnnouncementBanner (named export) ────────────────────────────────────────

export function AnnouncementBanner({
  collapsed = false,
  isMobile = false,
}: {
  /** Sidebar state, passed down from DashboardLayout exactly as the Topbar
   *  receives it. Without the matching left offset the banner sits UNDER the
   *  fixed sidebar (z-40) and its first ~256px — the whole title — is hidden. */
  collapsed?: boolean;
  isMobile?: boolean;
} = {}) {
  const { user } = useAuth();
  /* Which company's announcement. From the signed-in person, not a constant — phase 3. */
  const { workspaceId } = useWorkspaceId();
  const [announcement, setAnnouncement] = useState<Announcement | null>(null);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    /* No user or no workspace → nothing to ask for. The banner simply does not render. */
    if (!user || !workspaceId) return;

    async function fetchLatest() {
      const nowIso = new Date().toISOString();
      const { data, error } = await supabase
        .from('announcements')
        .select('*')
        .eq('workspace_id', workspaceId)
        .eq('is_active', true)
        /* Only PINNED announcements banner (Lis, 2026-08-06): unpinning an
           announcement in the Company → Announcements page stops it from
           showing at the top of every page without deleting it. Before this,
           any latest active announcement became a banner and the only way to
           silence one was deleting rows. */
        .eq('pinned', true)
        .or('expires_at.is.null,expires_at.gt.' + nowIso)
        .order('created_at', { ascending: false })
        .limit(1);

      if (error || !data || data.length === 0) return;

      const item = data[0] as Announcement;
      const dismissed = getDismissedIds();
      if (!dismissed.includes(item.id)) {
        setAnnouncement(item);
      }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- pre-existing, adding dep would cause re-render loop
    }

    fetchLatest();
  // eslint-disable-next-line react-hooks/exhaustive-deps -- pre-existing, adding dep would cause re-render loop
  }, [user]);

  function handleDismiss() {
    if (announcement) {
      addDismissedId(announcement.id);
    }
    setDismissed(true);
  }

  if (!user || !announcement || dismissed) return null;

  const cfg = TYPE_CONFIG[announcement.type] ?? TYPE_CONFIG.info;

  const iconMap: Record<AnnouncementType, React.ReactNode> = {
    info: <Info size={16} aria-label="Info" className={cfg.bannerIcon} />,
    warning: <AlertTriangle size={16} aria-label="Warning" className={cfg.bannerIcon} />,
    feature: <Sparkles size={16} aria-label="Feature" className={cfg.bannerIcon} />,
    maintenance: <Wrench size={16} aria-label="Maintenance" className={cfg.bannerIcon} />,
  };

  return (
    <div
      className={`fixed top-16 ${isMobile ? 'left-0' : collapsed ? 'left-16' : 'left-64'} right-0 z-30 border-l-4 ${cfg.bannerBorder} transition-all duration-300`}
      style={{ background: 'rgba(0,0,0,0.55)', backdropFilter: 'blur(8px)' }}
    >
      <div className="flex items-center gap-3 px-6 py-2.5 text-sm text-white max-w-full">
        {/* Icon */}
        <span className="flex-shrink-0">{iconMap[announcement.type]}</span>

        {/* Content */}
        <p className="flex-1 min-w-0 truncate">
          <strong className="font-semibold">{announcement.title}</strong>
          {' — '}
          <span className="text-white/80">{announcement.body}</span>
        </p>

        {/* Dismiss */}
        <button
          onClick={handleDismiss}
          aria-label="Dismiss announcement"
          className="flex-shrink-0 text-white/60 hover:text-white transition-colors p-1 rounded"
        >
          <X size={16} aria-label="Dismiss" />
        </button>
      </div>
    </div>
  );
}
